import { Badge } from '@startupquickstart/frontend';
import { cn } from '@/lib/utils';

interface ErrorSummary {
  byField: Record<string, number>;
  byCode: Record<string, number>;
}

interface ErrorSummaryCardsProps {
  summary: ErrorSummary;
  total: number;
  activeFieldFilter: string | null;
  onFieldFilterChange: (field: string | null) => void;
}

const codeColors: Record<string, string> = {
  REQUIRED: 'bg-red-100 text-red-700 hover:bg-red-200 border-red-200',
  INVALID_TYPE: 'bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-200',
  INVALID_FORMAT: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border-yellow-200',
  FK_NOT_FOUND: 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-200',
  DUPLICATE: 'bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-200',
  UNKNOWN: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-200',
};

const codeLabels: Record<string, string> = {
  REQUIRED: 'Required',
  INVALID_TYPE: 'Invalid Type',
  INVALID_FORMAT: 'Invalid Format',
  FK_NOT_FOUND: 'Not Found',
  DUPLICATE: 'Duplicate',
  UNKNOWN: 'Other',
};

/**
 * Error Summary Cards Component
 * Displays clickable badges showing error counts by field and type
 */
export function ErrorSummaryCards({
  summary,
  total,
  activeFieldFilter,
  onFieldFilterChange,
}: ErrorSummaryCardsProps) {
  const { byField, byCode } = summary;

  // Sort fields by count (descending)
  const sortedFields = Object.entries(byField).sort(([, a], [, b]) => b - a);
  const sortedCodes = Object.entries(byCode).sort(([, a], [, b]) => b - a);

  return (
    <div className="space-y-4">
      {/* Total errors header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-muted-foreground">
          {total} validation {total === 1 ? 'error' : 'errors'} found
        </h4>
        {activeFieldFilter && (
          <button
            onClick={() => onFieldFilterChange(null)}
            className="text-xs text-primary hover:underline"
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Error type badges */}
      {sortedCodes.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            By Error Type
          </p>
          <div className="flex flex-wrap gap-2">
            {sortedCodes.map(([code, count]) => (
              <span
                key={code}
                className={cn(
                  'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border',
                  codeColors[code] || codeColors.UNKNOWN,
                )}
              >
                {codeLabels[code] || code}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Field filter badges */}
      {sortedFields.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            By Field (click to filter)
          </p>
          <div className="flex flex-wrap gap-2">
            {sortedFields.map(([field, count]) => (
              <button
                key={field}
                onClick={() => onFieldFilterChange(activeFieldFilter === field ? null : field)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all',
                  activeFieldFilter === field
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted/50 text-foreground hover:bg-muted border-transparent hover:border-border',
                )}
              >
                <Badge variant="secondary" className="h-5 min-w-[1.25rem] px-1 text-[10px]">
                  {count}
                </Badge>
                {field}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
